import { Fragment, ReactNode, useCallback, useMemo, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';

import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { DesignAvatar } from '@components/design-avatar';
import { IconButton } from '@components/icon-button';
import { IconDateSmall } from '@components/icons/date-small';
import { IconPerson } from '@components/icons/person';
import { IconRemove } from '@components/icons/remove';
import { IconRename } from '@components/icons/rename';
import { IconSidemenuRightOpen } from '@components/icons/sidemenu-right-open';
import { IconStatus } from '@components/icons/status';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { Menu } from '@components/menu';
import { SquareImage } from '@components/square-image';
import { Horizontal, Stack } from '@components/stack';
import { ColumnIcon, SmallColumnIcon } from '@components/table/column-icon';
import { TableStore } from '@components/table/table-store';
import { AvailableColumn, TableView } from '@components/table/types';
import { Text } from '@components/text';
import { showError, showSuccess } from '@components/toaster';
import { useEvent } from '@hooks/use-event';

import {
  AttributeTemplate,
  AttributeType,
  TravellerAttributeTemplate,
} from 'api/attributes/requests';
import { useEditCompanyTraveller } from 'api/company-traveller/hooks';
import { waitForElasticRefresh } from 'api/hooks';
import { queryClient } from 'api/query-client';
import { useFetchTravellerFields } from 'api/traveller-journeys/hooks';
import { editJourneyTraveller } from 'api/traveller-journeys/requests';
import {
  ActionsDataAction,
  ActionsDataField,
  JourneyType,
  TravellerJourneyListItem,
} from 'api/types';
import { User } from 'api/users/requests';
import { TableUserCell } from 'components/table-user-cell';
import { getActionLabel } from 'ui/actions/action-config';
import { ActionIcon } from 'ui/actions/action-config';
import { ActionLabel } from 'ui/actions/action-config';

import { IssuesCell } from './cells/issues';
import { NameCell } from './cells/name';
import { PhoneCell } from './cells/phone';
import { ProgressCell } from './cells/progress';
import { StatusCell } from './cells/status';
import { actionStatusLabelMap, statusLabelMap, statusTagMap } from './constants';
import { ExtraStateStore } from './extra-state-store';
import { t } from './locale';
import { ShowCustomNameModal, useSetCustomNameModal } from './set-custom-name-modal';
import { TravellersColumnControlStore } from './travellers-column-control-store';

import { ShowCompanyTravellerToJourneySidebar } from '../../travellers/company-traveller-to-journey';

export const createBaseColumns = (): Array<AvailableColumn<TravellerJourneyListItem>> => {
  return [
    {
      id: 'name',
      title: t('core.fullName'),
      type: 'text',
      renderHeaderIcon: () => <IconPerson size={16} />,
      Cell: NameCell,
    },
    {
      id: 'email',
      title: t('core.email'),
      type: 'email',
    },
    {
      id: 'phone',
      title: t('core.phone'),
      type: 'phone_number',
      Cell: PhoneCell,
    },
    {
      id: 'progress',
      title: 'Progress',
      type: 'progress',
      Cell: ProgressCell,
    },
    {
      id: 'status',
      title: t('status'),
      type: 'single_select',
      renderHeaderIcon: () => <IconStatus size={16} />,
      labelMap: statusLabelMap,
      Cell: StatusCell,
    },
    {
      id: 'dateUpdated',
      title: t('date_modified'),
      type: 'date',
    },
    {
      id: 'dateCreated',
      title: t('date_created'),
      type: 'date',
    },
    {
      id: 'issues',
      title: t('issues'),
      type: 'issues',
      Cell: IssuesCell,
    },
  ];
};

export const useAttributeColumns = ({
  attributes,
  usersData = [],
  showEditTravellerSidebar,
  editableJourneyAttributes = true,
  editableCompanyTravellerAttributes = true,
}: {
  attributes: Array<AttributeType | TravellerAttributeTemplate>;
  usersData?: Array<User>;
  showEditTravellerSidebar: ShowCompanyTravellerToJourneySidebar;
  editableJourneyAttributes?: boolean;
  editableCompanyTravellerAttributes?: boolean;
}): Array<AvailableColumn<TravellerJourneyListItem>> => {
  const editCompanyTraveller = useEditCompanyTraveller();

  return useMemo(() => {
    return attributes.map((attribute) => {
      const attributeId = makeAttributeId(
        attribute.shortId,
        Boolean(attribute.isTravellerAttribute)
      );
      const isEditable = attribute.isTravellerAttribute
        ? editableCompanyTravellerAttributes
        : editableJourneyAttributes;
      return {
        id: attributeId,
        title: attribute.title,
        type: attribute.attributeType,
        onMultiFilesClick: (traveller) => {
          showEditTravellerSidebar({
            journeyShortId: traveller.journey,
            journeyTravellerId: traveller.shortId,
          });
        },
        Cell:
          attribute.attributeType === 'user'
            ? ({ row }) => <TableUserCell row={row} attribute={attribute} users={usersData} />
            : undefined,
        defaultHidden: true,
        onEdit:
          isEditable && ['text', 'paragraph'].includes(attribute.attributeType)
            ? async (row, value: string) => {
                const formData = new FormData();
                if (attribute.isTravellerAttribute) {
                  formData.append(`company_traveller_attribute__${attribute.shortId}`, value);
                  await editCompanyTraveller.mutateAsync({
                    shortId: row.companyTravellerShortId,
                    data: formData,
                  });
                } else {
                  formData.append(`journey_attribute__${attribute.shortId}`, value);
                  // Cannot use the editJourneyTraveller hook for this, since we don't know
                  // the traveller journey shortId and the journeyId at the root of this hook
                  await editJourneyTraveller(row.shortId, formData);
                  await waitForElasticRefresh(() => {
                    queryClient.invalidateQueries(['travellers', row.journey]);
                    queryClient.invalidateQueries(['traveller', row.shortId]);
                  });
                  showSuccess(t('travellerEdited'));
                }
              }
            : undefined,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes, usersData]);
};

export const useActionColumns = ({
  actionsData,
  extraStateStore,
  onPersist,
  columnControlStore,
  tableStore,
}: {
  actionsData: JourneyType['actionsData'];
  extraStateStore: ExtraStateStore;
  onPersist(newState: TableView): void;
  columnControlStore: TravellersColumnControlStore;
  tableStore: TableStore;
}) => {
  const showSetCustomNameModal = useSetCustomNameModal();
  const { useAddedActionIds, useCustomNames, actions } = extraStateStore;
  const addedActionIds = useAddedActionIds();
  const customNames = useCustomNames();

  const onCustomNameChange = useCallback(
    (id: string, name?: string) => {
      if (!name) {
        actions.removeCustomName(id);
      } else {
        actions.setCustomName(id, name);
      }
      onPersist(tableStore.actions.getState());
    },
    [actions, onPersist, tableStore.actions]
  );

  const onActionClick = useCallback(
    (id: string) => {
      columnControlStore.actions.focusColumn(id);
    },
    [columnControlStore.actions]
  );

  return useMemo(() => {
    const columns: Array<AvailableColumn<TravellerJourneyListItem>> = [];

    (actionsData ?? []).forEach((action) => {
      if (addedActionIds.includes(makeColumnId(action.shortId, 'date_completed'))) {
        columns.push(
          createDateCompletedColumn({
            action,
            customNames,
            onCustomNameChange,
            showSetCustomNameModal,
            onActionClick,
            onRemove: () =>
              columnControlStore.actions.toggleColumn(
                makeColumnId(action.shortId, 'date_completed'),
                false
              ),
          })
        );
      }

      if (addedActionIds.includes(makeColumnId(action.shortId, 'status'))) {
        columns.push(
          createStatusColumn({
            action,
            customNames,
            onCustomNameChange,
            showSetCustomNameModal,
            onActionClick,
            onRemove: () =>
              columnControlStore.actions.toggleColumn(
                makeColumnId(action.shortId, 'status'),
                false
              ),
          })
        );
      }

      Object.entries(action.variables).forEach(([_, { fields: variables }]) => {
        variables.forEach((variable) => {
          const columnId = makeVariableColumnId(action.shortId, variable.id);
          if (addedActionIds.includes(columnId)) {
            columns.push(
              createVariableColumn({
                action,
                variable,
                columnId,
                customNames,
                onCustomNameChange,
                showSetCustomNameModal,
                onActionClick,
                onRemove: () => columnControlStore.actions.toggleColumn(columnId, false),
              })
            );
          }
        });
      });
    });

    return columns;
  }, [
    actionsData,
    addedActionIds,
    customNames,
    onCustomNameChange,
    showSetCustomNameModal,
    onActionClick,
    columnControlStore.actions,
  ]);
};

const createTooltip = (action: ActionsDataAction, label: string, icon: JSX.Element) => {
  return (
    <Fragment>
      <Stack space={1} orientation="horizontal" align="center">
        <ActionIcon action={action.action} size={16} color="white100" />
        <ActionLabel action={action.action} color="white100" />
      </Stack>
      <Stack space={1} orientation="horizontal" align="center">
        <IconSubIndicator size={16} color="white100" />
        {icon}
        <Text color="white100" font="regular12" text={label} />
      </Stack>
    </Fragment>
  );
};

type MenuHeaderParams = {
  action: ActionsDataAction;
  columnId: string;
  defaultName: string;
  customNames: Record<string, string>;
  onCustomNameChange: (id: string, name?: string) => void;
  showSetCustomNameModal: ShowCustomNameModal;
  onActionClick: (id: string) => void;
  icon?: ReactNode;
  closeMenu(): void;
};

const createMenuHeader = ({
  action,
  columnId,
  defaultName,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  closeMenu,
  icon,
}: MenuHeaderParams) => {
  const title = action.name ?? getActionLabel(action.action);
  const actionIcon = action.avatar ? (
    <DesignAvatar src={action.avatar} size={16} />
  ) : action.logo ? (
    <SquareImage src={action.logo} size={16} />
  ) : (
    <ActionIcon action={action.action} size={16} />
  );
  const card = (
    <Card spaceX={4} spaceY={2}>
      <Horizontal space={1} justify="space-between" align="center">
        <div>
          <Stack space={1} orientation="horizontal" align="center">
            {actionIcon}
            <Text font="regular12" text={title} />
          </Stack>
          <Stack space={1} orientation="horizontal" align="center">
            <IconSubIndicator size={16} color="purple50" />
            {icon ?? <IconDateSmall color="elementPurple50" />}
            <Text font="regular12" text={defaultName} />
          </Stack>
        </div>
        <IconButton
          icon={<IconSidemenuRightOpen />}
          variant="ghost-purple"
          size="md"
          onClick={() => {
            onActionClick(columnId);
            closeMenu();
          }}
        />
      </Horizontal>
    </Card>
  );

  return (
    <Stack space={2}>
      {card}
      <Menu.Item
        icon={<IconRename color="elementPurple80" />}
        label={t('customName')}
        onSelect={() => {
          showSetCustomNameModal({
            name: customNames[columnId],
            defaultName,
            card,
            onConfirm: (newName: string) => {
              if (newName === customNames[columnId]) {
                return;
              }
              onCustomNameChange(columnId, newName === defaultName ? undefined : newName);
            },
          });
        }}
      />
    </Stack>
  );
};

type ColumnParams = {
  action: ActionsDataAction;
  customNames: Record<string, string>;
  onCustomNameChange: (id: string, name?: string) => void;
  showSetCustomNameModal: ShowCustomNameModal;
  onActionClick: (id: string) => void;
  onRemove?: () => void;
};

type VariableColumnParams = ColumnParams & {
  variable: ActionsDataField;
  columnId: string;
};

const createDateCompletedColumn = ({
  action,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  onRemove,
}: ColumnParams): AvailableColumn<TravellerJourneyListItem> => {
  const id = makeColumnId(action.shortId, 'date_completed');
  const defaultName = t('action_date_completed');

  return {
    id,
    type: 'date',
    title: customNames[id] ?? defaultName,
    renderFallback: () => '-',
    menuHeader: (closeMenu) => {
      return createMenuHeader({
        action,
        columnId: id,
        defaultName,
        customNames,
        onCustomNameChange,
        showSetCustomNameModal,
        onActionClick,
        closeMenu,
      });
    },
    tooltip: () => {
      return createTooltip(
        action,
        t('action_date_completed'),
        <SmallColumnIcon type="date" color="white100" />
      );
    },
    menuFooter: (
      <Menu.Item
        label={t('removeField')}
        variant="danger"
        icon={<IconRemove />}
        onSelect={() => onRemove?.()}
      />
    ),
  };
};

const createStatusColumn = ({
  action,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  onRemove,
}: ColumnParams): AvailableColumn<TravellerJourneyListItem> => {
  const id = makeColumnId(action.shortId, 'status');
  const defaultName = t('action_status');

  return {
    id,
    type: 'single_select',
    labelMap: actionStatusLabelMap,
    title: customNames[id] ?? defaultName,
    renderFallback: () => '-',
    renderHeaderIcon: () => <IconStatus size={16} color="elementPurple50" />,
    menuHeader: (closeMenu) => {
      return createMenuHeader({
        action,
        columnId: id,
        defaultName,
        customNames,
        onCustomNameChange,
        showSetCustomNameModal,
        onActionClick,
        closeMenu,
        icon: <IconStatus size={16} color="elementPurple50" />,
      });
    },
    tooltip: () => {
      return createTooltip(action, t('action_status'), <IconStatus size="16" color="white100" />);
    },
    Cell: ({ row }) => {
      const status = row[makeColumnId(action.shortId, 'status') as keyof typeof row];
      return (
        <Badge
          variant={statusTagMap[status as keyof typeof statusTagMap]}
          text={actionStatusLabelMap[status as keyof typeof actionStatusLabelMap]}
        />
      );
    },
    menuFooter: (
      <Menu.Item
        label={t('removeField')}
        variant="danger"
        icon={<IconRemove />}
        onSelect={() => onRemove?.()}
      />
    ),
  };
};

const createVariableColumn = ({
  action,
  variable,
  columnId,
  customNames,
  onCustomNameChange,
  showSetCustomNameModal,
  onActionClick,
  onRemove,
}: VariableColumnParams): AvailableColumn<TravellerJourneyListItem> => {
  const defaultName = variable.label;

  return {
    id: columnId,
    type: variable.type,
    title: customNames[columnId] ?? defaultName,
    renderFallback: () => '-',
    renderHeaderIcon: () => <ColumnIcon type={variable.type} color="elementPurple50" size={16} />,
    tooltip: () => {
      return createTooltip(
        action,
        variable.label,
        <SmallColumnIcon type={variable.type} color="white100" />
      );
    },
    menuHeader: (closeMenu) => {
      return createMenuHeader({
        action,
        columnId,
        defaultName,
        customNames,
        onCustomNameChange,
        showSetCustomNameModal,
        onActionClick,
        closeMenu,
        icon: <ColumnIcon type={variable.type} size={16} />,
      });
    },
    menuFooter: (
      <Menu.Item
        label={t('removeField')}
        variant="danger"
        icon={<IconRemove />}
        onSelect={() => onRemove?.()}
      />
    ),
  };
};

export const makeColumnId = (actionId: string, ...rest: string[]) => {
  const base = `action__${actionId}`;
  if (rest.length === 0) {
    return base;
  }
  return `${base}__${rest.join('__')}`;
};

export const makeVariableColumnId = (actionId: string, ...rest: string[]) => {
  return makeColumnId(actionId, 'vars', ...rest);
};

export const makeAttributeId = (
  shortId: AttributeTemplate['shortId'],
  isTravellerAttribute: boolean
) => {
  return isTravellerAttribute ? `attribute-${shortId}` : `journey-attribute-${shortId}`;
};

export const useDebouncedFieldsFetch = (journeyId: string) => {
  const fetchFieldsMutation = useFetchTravellerFields(journeyId);
  const queryClient = useQueryClient();
  const pendingFieldsRef = useRef<Set<string>>(new Set());

  const debouncedFetch = useMemo(
    () =>
      debounce(() => {
        const fieldsToFetch = Array.from(pendingFieldsRef.current);
        pendingFieldsRef.current.clear();

        if (fieldsToFetch.length === 0) return;

        fetchFieldsMutation.mutate(fieldsToFetch, {
          onSuccess: (data) => {
            queryClient.setQueryData<TravellerJourneyListItem[]>(
              ['travellers', journeyId],
              (oldData) => {
                if (!oldData) return oldData;

                const fieldsMap = new Map<string, Record<string, any>>();
                data.forEach(({ travellerId, fields }) => {
                  fieldsMap.set(travellerId, fields);
                });

                return oldData.map((traveller) => {
                  const newFields = fieldsMap.get(traveller.shortId);
                  if (!newFields) return traveller;

                  return {
                    ...traveller,
                    actionsData: {
                      ...traveller.actionsData,
                      ...newFields,
                    },
                  };
                });
              }
            );
          },
          onError: () => {
            showError(t('columnLoadError'));
          },
        });
      }, 1000),
    [fetchFieldsMutation, queryClient, journeyId]
  );

  const handleFieldsNeeded = useEvent((newFieldIds: string[]) => {
    newFieldIds.forEach((id) => pendingFieldsRef.current.add(id));
    debouncedFetch();
  });

  return handleFieldsNeeded;
};
